import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { WizardOption } from 'src/app/models/wizard/wizard-option';
import { WizardStep } from 'src/app/models/wizard/wizard-step';
import { AnalyticsService, AnalyticsUtility } from 'src/app/services/analytics/analytics.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { WizardStepName } from 'src/app/services/wizard/wizard-step-name.enum';
import { WizardService } from 'src/app/services/wizard/wizard.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-need-to-run-step',
  templateUrl: './need-to-run-step.component.html',
  styleUrls: ['./need-to-run-step.component.scss']
})
export class NeedToRunStepComponent implements OnInit {
  currentStep: WizardStep = new WizardStep();
  stepNumber: number = 0;
  title: string = "";
  subtitleBeginning: string = "";
  subtitleMiddle: string = "";
  subtitleEnd: string = "";
  i18nSubtitle: string = "";
  isEnglish: boolean = true;
  needToRunOptions: WizardOption[] = [];
  isContinueBtnDisabled: boolean = true;
  isEducation: boolean = false;
  analyticsStepName: string = AnalyticsUtility.wizardStepName.needToRun;

  constructor(
    private analyticsService: AnalyticsService,
    public wizardService: WizardService,
    private route: ActivatedRoute,
    private confSvc: ConfigService,
    private utilityService: UtilityService) { }

  async ngOnInit(): Promise<void> {
    await this.confSvc.setup();

    this.currentStep = this.wizardService.getStep(WizardStepName.NeedToRun);
    this.stepNumber = this.wizardService.getStepNumber(this.currentStep.name);
    this.title = this.confSvc.getString("needToRunTitle");
    this.subtitleBeginning = this.confSvc.getString("wizardStepSubtitleBeginning");
    this.subtitleMiddle = this.confSvc.getString("singleSelectionWizardStepSubtitleMiddle");
    this.subtitleEnd = this.confSvc.getString("singleSelectionWizardStepSubtitleEnd");
    this.i18nSubtitle = this.confSvc.getString("i18nSingleSelectionWizardStepSubtitle");

    this.isEnglish = this.utilityService.getLocaleInfo().isEnglish;

    this.route.queryParams.subscribe(params => {
      this.wizardService.setSelectedOptionsFromUrlIfPresent(params);
      this.setNeedToRunOptions();
    });

    this.analyticsService.logCurrentPage(this.analyticsStepName);
  }

  setNeedToRunOptions(): void {
    this.wizardService.getSelectedWizardOptionsForStep(WizardStepName.PrimaryUse)
      .pipe(
        switchMap((primaryUseOptions: WizardOption[]) => {
          const primaryUseTitle = primaryUseOptions[0]?.title ? primaryUseOptions[0].title : "";
          this.isEducation = primaryUseTitle === this.confSvc.getString("primaryUseEducationTitle");

          return this.wizardService.getSelectedWizardOptionsForStep(this.currentStep.name);
        })
      )
      .subscribe((needToRunOptions: WizardOption[]) => {
        this.isContinueBtnDisabled = needToRunOptions.length === 0;

        if (this.isEducation) {
          this.buildEducationNeedToRunOptions(needToRunOptions);
        } else {
          this.buildEnterpriseNeedToRunOptions(needToRunOptions);
        }
      });
  }

  buildEducationNeedToRunOptions(selectedOptions: WizardOption[]) {
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunSimpleTitleEdu"), this.confSvc.getStringArray("needToRunOptionsToDisplayEdu"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunSimpleTitleEdu"),
        subtitle: this.confSvc.getString("needToRunSimpleSubtitleEdu"),
        iconFontSet: 'fas',
        iconName: '',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunBrowsingOptionEnterpriseTitle"))
      }));
    }
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunAdvancedTitleEdu"), this.confSvc.getStringArray("needToRunOptionsToDisplayEdu"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunAdvancedTitleEdu"),
        subtitle: this.confSvc.getString("needToRunAdvancedSubtitleEdu"),
        iconFontSet: 'fas',
        iconName: '',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunConferencingOptionEnterpriseTitle"))
      }));
    }
  }

  /**
    * Functions for displaying the need to run cards for Enterprise and healthcare.
    */
  buildEnterpriseNeedToRunOptions(selectedOptions: WizardOption[]) {
    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunSimpleTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEnterprise"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunSimpleTitle"),
        subtitle: this.confSvc.getString("needToRunSimpleSubtitle"),
        iconFontSet: 'fas',
        iconName: '',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunBrowsingOptionEnterpriseTitle"))
      }));
    }

    if (this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunAdvancedTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEnterprise"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunAdvancedTitle"),
        subtitle: this.confSvc.getString("needToRunAdvancedSubtitle"),
        iconFontSet: 'fas',
        iconName: '',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunConferencingOptionEnterpriseTitle"))
      }));
    }
  }

  /**
    * Util functions
    */
  onSelect(event: WizardOption): void {
    this.isContinueBtnDisabled = !event.isSelected;
    this.needToRunOptions.map(item => {
      if (item.title !== event.title) {
        item.isSelected = false;
        this.wizardService.removeSelectedWizardOption(item.title);
      } else {
        item.isSelected = event.isSelected;
      }
    });
    if (event.isSelected) {
      this.wizardService.addSelectedWizardOption(event);
    }
  }

  onSkip(): void {
    this.needToRunOptions.forEach((option) => {
      this.wizardService.addSelectedWizardOption(option)
    })
  }
}
